
export class ApiError extends Error {
  status: number
  detail: any
  constructor(message: string, status: number, detail?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}
/**
 * API client for ScribeyMD backend.
 */

const API_BASE = '/api/v1'

class ApiClient {
  private token: string | null = null

  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('scribey_token', token)
    } else {
      localStorage.removeItem('scribey_token')
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('scribey_token')
    }
    return this.token
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    }

    const token = this.getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const res = await fetch(`${API_BASE}${path}`, { ...options, headers })

    if (res.status === 401) {
      this.setToken(null)
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      const detail = body.detail
      const msg = typeof detail === 'string'
        ? detail
        : (detail && detail.message) || `API error: ${res.status}`
      const err = new ApiError(msg, res.status, detail)
      throw err
    }

    if (res.status === 204) return undefined as T
    return res.json()
  }

  // Auth
  async googleLogin(credential: string) {
    return this.request<{ access_token: string; doctor: any }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    })
  }

  async register(data: { email: string; password: string; name: string; specialty: string; practice_size?: string; current_emr?: string; agree_terms: boolean; agree_privacy: boolean; agree_baa: boolean }) {
    return this.request<{ access_token: string; doctor: any }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async forgotPassword(email: string) {
    return this.request<{ status: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async resetPassword(email: string, code: string, newPassword: string) {
    return this.request<{ status: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, code, new_password: newPassword }),
    })
  }

  async login(email: string, password: string) {
    return this.request<{ access_token: string; doctor: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  async getCognitoLoginUrl(redirectUri: string) {
    return this.request<{ url: string }>(`/auth/cognito-login-url?redirect_uri=${encodeURIComponent(redirectUri)}`)
  }

  async cognitoCallback(code: string, redirectUri: string) {
    return this.request<{ access_token: string; doctor: any }>('/auth/cognito-callback', {
      method: 'POST',
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    })
  }

  async me() {
    return this.request<any>('/auth/me')
  }

  async logoutAll() {
    return this.request<{ status: string; invalidated_at: string }>('/auth/logout-all', { method: 'POST' })
  }

  // --- Device Authorization Grant (shared/public computer login) ---
  async deviceStart(label?: string) {
    return this.request<{
      device_code: string; user_code: string;
      verification_uri: string; verification_uri_complete: string;
      expires_in: number; interval: number;
    }>('/auth/device/start', { method: 'POST', body: JSON.stringify({ label: label || null }) })
  }

  async devicePoll(deviceCode: string) {
    // public endpoint; bypass the 401->redirect behavior of request()
    const res = await fetch(`${API_BASE}/auth/device/poll?device_code=${encodeURIComponent(deviceCode)}`)
    return res.json() as Promise<{ status: string; access_token?: string; doctor?: any }>
  }

  async deviceApprove(userCode: string) {
    return this.request<{ status: string; label?: string; ip?: string }>('/auth/device/approve', {
      method: 'POST', body: JSON.stringify({ user_code: userCode }),
    })
  }

  async deviceDeny(userCode: string) {
    return this.request<{ status: string }>('/auth/device/deny', {
      method: 'POST', body: JSON.stringify({ user_code: userCode }),
    })
  }

  async updateProfile(data: { name?: string; specialty?: string; subspecialty?: string; current_emr?: string; practice_size?: string; transcript_retention_days?: number; recording_ack?: boolean }) {
    return this.request<any>('/auth/me', { method: 'PATCH', body: JSON.stringify(data) })
  }

  // Phase 2: dry-run (default) or execute the transcript/critic-flag purge for the current doctor.
  async runRetentionPurge(dryRun = true) {
    return this.request<any>(`/auth/retention/purge?dry_run=${dryRun}`, { method: 'POST' })
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/change-password', {
      method: 'PATCH',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    })
  }

  // Encounters
  async listEncounters(limit = 50, offset = 0) {
    return this.request<{ encounters: any[]; total: number }>(
      `/encounters?limit=${limit}&offset=${offset}`
    )
  }

  async createEncounter(data: any) {
    return this.request<any>('/encounters', { method: 'POST', body: JSON.stringify(data) })
  }

  async getEncounter(id: string) {
    return this.request<any>(`/encounters/${id}`)
  }

  async deleteEncounter(id: string) {
    return this.request<void>(`/encounters/${id}`, { method: 'DELETE' })
  }

  async setCharted(id: string, charted: boolean) {
    return this.request<any>(`/encounters/${id}/charted`, {
      method: 'PATCH',
      body: JSON.stringify({ charted }),
    })
  }

  async deleteAccount() {
    return this.request<{ status: string }>('/auth/account', { method: 'DELETE' })
  }

  async exportAccountData() {
    return this.request<any>('/auth/account/export')
  }

  async getRecoveryKey(encounterId: string): Promise<{ key_b64: string }> {
    return this.request(`/encounters/${encounterId}/recovery-key`)
  }

  async updateEncounterStatus(id: string, status: string) {
    return this.request<any>(`/encounters/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) })
  }

  async updatePatient(id: string, data: { first_name?: string; last_name?: string; dob?: string | null; mrn?: string; sex?: string; ai_consent?: boolean; ai_consent_source?: string }) {
    return this.request<any>(`/patients/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async updateEncounter(id: string, data: { patient_name?: string; encounter_type?: string | null; procedure_name?: string; patient_id?: string; transcript?: string; patient_dob?: string; confirm_patient_id?: string; force_new_patient?: boolean; canonical_name?: string; ai_consent_override?: boolean }) {
    return this.request<any>(`/encounters/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async updateEncounterTranscript(id: string, transcript: string) {
    return this.request<any>(`/encounters/${id}`, { method: 'PATCH', body: JSON.stringify({ transcript }) })
  }

  // Patients
  async listPatients(query?: string, limit = 50, sort = 'last_name', offset = 0) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    if (query) params.set('q', query)
    if (sort) params.set('sort', sort)
    return this.request<{ patients: any[]; total: number }>(`/patients?${params}`)
  }

  async createPatient(data: { first_name: string; last_name: string; dob?: string; mrn?: string; sex?: string }) {
    return this.request<any>('/patients', { method: 'POST', body: JSON.stringify(data) })
  }

  async scanPatientImage(imageBase64: string): Promise<{ first_name?: string; last_name?: string; dob?: string; mrn?: string; sex?: string }> {
    return this.request<any>('/patients/scan', { method: 'POST', body: JSON.stringify({ image: imageBase64 }) })
  }

  async deletePatient(id: string) {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = localStorage.getItem('scribey_token')
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${API_BASE}/patients/${id}`, {
      method: 'DELETE',
      headers,
    })
    if (!res.ok) throw new Error('Failed to delete patient')
  }

  async getPatient(id: string) {
    return this.request<any>(`/patients/${id}`)
  }

  async getPatientEncounters(patientId: string) {
    return this.request<any[]>(`/patients/${patientId}/encounters`)
  }

  // Notes
  async generateNote(encounterId: string, maxIterations = 3) {
    return this.request<any>(`/notes/encounters/${encounterId}/generate`, {
      method: 'POST',
      body: JSON.stringify({ max_iterations: maxIterations }),
    })
  }

  async reviseNote(noteId: string, feedback: string) {
    return this.request<any>(`/notes/notes/${noteId}/revise`, {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    })
  }

  // Fast transcribe-only: returns {transcript} in ~1s so the overlay can show immediately.
  async voiceTranscribe(noteId: string, blob: Blob): Promise<{ transcript: string }> {
    const token = this.getToken()
    const formData = new FormData()
    formData.append('audio', blob, 'voice-edit.pcm')
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${API_BASE}/notes/notes/${noteId}/voice-transcribe`, {
      method: 'POST',
      headers,
      body: formData,
    })
    if (res.status === 401) {
      this.setToken(null)
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new ApiError((body as any).detail || `API error: ${res.status}`, res.status, (body as any).detail)
    }
    return res.json()
  }

  async voiceReviseNote(noteId: string, blob: Blob): Promise<any> {
    const token = this.getToken()
    const formData = new FormData()
    formData.append('audio', blob, 'voice-edit.webm')
    // Do NOT set Content-Type — let browser set it with the boundary
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${API_BASE}/notes/notes/${noteId}/voice-revise`, {
      method: 'POST',
      headers,
      body: formData,
    })
    if (res.status === 401) {
      this.setToken(null)
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error((body as any).detail || `API error: ${res.status}`)
    }
    return res.json()
  }

  async listNotes(encounterId: string) {
    return this.request<any[]>(`/notes/encounters/${encounterId}/notes`)
  }

  async approveNote(noteId: string) {
    return this.request<any>(`/notes/notes/${noteId}/approve`, {
      method: 'PATCH',
    })
  }

  async unapproveNote(noteId: string) {
    return this.request<any>(`/notes/notes/${noteId}/unapprove`, {
      method: 'PATCH',
    })
  }

  async saveEditDiffs(noteId: string, diffs: Array<{ section: string; before_text: string; after_text: string }>) {
    return this.request<any>(`/notes/notes/${noteId}/diffs`, {
      method: 'POST',
      body: JSON.stringify({ diffs }),
    })
  }

  async transcribeVoiceRevision(audioBlob: Blob): Promise<{ text: string }> {
    const formData = new FormData()
    formData.append('audio', audioBlob, 'revision.webm')
    const token = this.getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${API_BASE}/notes/voice-revision/transcribe`, {
      method: 'POST', headers, body: formData
    })
    if (!res.ok) throw new Error('Transcription failed')
    return res.json()
  }

  /**
   * Stream note generation via SSE. Uses fetch + ReadableStream for POST + auth headers.
   */
  generateNoteStream(
    encounterId: string,
    onEvent: (event: { type: string; [key: string]: any }) => void,
    patientId?: string,
  ): { abort: () => void } {
    const controller = new AbortController()

    const token = this.getToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`

    ;(async () => {
      try {
        const res = await fetch(`${API_BASE}/notes/encounters/${encounterId}/generate-stream`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            max_iterations: 3,
            intake_session_id: patientId
              ? (() => { try { return localStorage.getItem(`scribey_ctx_intake_${patientId}`) || null } catch { return null } })()
              : null,
          }),
          signal: controller.signal,
        })

        if (res.status === 401) {
          this.setToken(null)
          window.location.href = '/login'
          return
        }

        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          onEvent({ type: 'error', message: body.detail || `API error: ${res.status}` })
          return
        }

        const reader = res.body?.getReader()
        if (!reader) return

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // Parse SSE events from buffer
          const parts = buffer.split('\n\n')
          buffer = parts.pop() || ''

          for (const part of parts) {
            const lines = part.split('\n')
            let data = ''
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                data += line.slice(6)
              }
            }
            if (data) {
              try {
                onEvent(JSON.parse(data))
              } catch {
                // skip malformed events
              }
            }
          }
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          onEvent({ type: 'error', message: err.message || 'Stream failed' })
        }
      }
    })()

    return { abort: () => controller.abort() }
  }

  // Agent chat
  async agentMessage(message: string, history: Array<{ role: string; content: string }> = [], encounterId?: string) {
    return this.request<{ response: string; note_updated?: boolean; revised_note?: any }>('/agent/message', {
      method: 'POST',
      body: JSON.stringify({ message, history, ...(encounterId ? { encounter_id: encounterId } : {}) }),
    })
  }

  // Billing
  async getBillingStatus(): Promise<{
    subscription_status: string
    entitled: boolean
    trial_encounters_used: number
    trial_encounters_limit: number
    trial_remaining: number
    has_subscription: boolean
    tier: string
  }> {
    return this.request('/billing/status')
  }

  async createCheckout(priceLookupKey: string = 'core_monthly'): Promise<{ checkout_url: string; session_id: string }> {
    return this.request('/billing/checkout', { 
      method: 'POST',
      body: JSON.stringify({ price_lookup_key: priceLookupKey })
    })
  }

  async createPortalSession(): Promise<{ portal_url: string }> {
    return this.request('/billing/portal', { method: 'POST' })
  }

  async syncSubscription() {
    return this.request<any>('/billing/sync', { method: 'POST' })
  }

  // Templates
  async listStarterTemplates(specialty?: string, noteType?: string) {
    const params = new URLSearchParams()
    if (specialty) params.set('specialty', specialty)
    if (noteType) params.set('note_type', noteType)
    const query = params.toString()
    return this.request<any[]>(`/templates/starters${query ? `?${query}` : ''}`)
  }

  async importTemplate(starterId: string) {
    return this.request<any>(`/templates/import/${starterId}`, { method: 'POST' })
  }

  async listMyTemplates() {
    return this.request<any[]>('/templates/mine')
  }

  async createCustomTemplate(data: { name: string; description?: string; note_type: string; example_note?: string; instructions?: string }) {
    return this.request<any>('/templates/mine', { method: 'POST', body: JSON.stringify(data) })
  }

  async updateMyTemplate(id: string, data: { name?: string; description?: string; example_note?: string; instructions?: string; sections?: any; is_default?: boolean }) {
    return this.request<any>(`/templates/mine/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  }

  async deleteMyTemplate(id: string) {
    return this.request<any>(`/templates/mine/${id}`, { method: 'DELETE' })
  }

  // Coding & Instructions
  async getCodes(noteId: string) {
    return this.request<any>(`/coding/notes/${noteId}/suggested-codes`)
  }

  async getCodingAnalysis(noteId: string) {
    return this.request<any>(`/coding/notes/${noteId}/analysis`)
  }

  async getUpcodingSuggestions(noteId: string) {
    return this.request<any>(`/coding/notes/${noteId}/upcoding`)
  }

  async getPriorAuthGuidance(noteId: string) {
    return this.request<any>(`/coding/notes/${noteId}/prior-auth`)
  }

  async generateLetter(noteId: string, letterType: string, additionalContext: Record<string, string> = {}) {
    return this.request<{ content: string; letter_type: string }>(`/letters/notes/${noteId}/generate`, {
      method: 'POST',
      body: JSON.stringify({ letter_type: letterType, additional_context: additionalContext }),
    })
  }

  async getPatientInstructions(encounterId: string) {
    return this.request<any>(`/instructions/${encounterId}`)
  }

  // Learning & Preferences
  async getLearningProgress() {
    return this.request<any>('/learning/progress')
  }

  async getEditPatterns(noteType?: string) {
    const params = noteType ? `?note_type=${noteType}` : ''
    return this.request<any>(`/learning/edit-patterns${params}`)
  }

  async getStyleProfiles() {
    return this.request<any>('/learning/style-profiles')
  }

  async getPreferences() {
    return this.request<any>('/learning/preferences')
  }

  async updatePreferences(data: { preferences?: Record<string, any>; settings?: Record<string, any> }) {
    return this.request<any>('/learning/preferences', { method: 'PATCH', body: JSON.stringify(data) })
  }

  async getPhrases() {
    return this.request<any>('/learning/phrases')
  }

  async savePhrase(name: string, text: string, noteType?: string) {
    return this.request<any>('/learning/phrases', {
      method: 'POST',
      body: JSON.stringify({ name, text, note_type: noteType }),
    })
  }

  async deletePhrase(phraseId: string) {
    return this.request<void>(`/learning/phrases/${phraseId}`, { method: 'DELETE' })
  }

  async deleteStyleProfile(profileId: string) {
    return this.request<any>(`/learning/style-profiles/${profileId}`, { method: 'DELETE' })
  }

  async deleteStyleRule(profileId: string, ruleKey: string) {
    return this.request<any>(`/learning/style-profiles/${profileId}/delete-rule`, {
      method: 'PATCH',
      body: JSON.stringify({ rule_key: ruleKey }),
    })
  }

  // Intake
  async createIntakeSession(data: { 
    patient_id?: string; 
    visit_reason?: string; 
    pin?: string; 
    expires_hours?: number 
  }) {
    return this.request<any>('/intake/sessions', { method: 'POST', body: JSON.stringify(data) })
  }

  async listIntakeSessions(statusFilter?: string, limit = 50, offset = 0) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    if (statusFilter) params.set('status_filter', statusFilter)
    return this.request<any[]>(`/intake/sessions?${params}`)
  }

  async getIntakeSession(sessionId: string) {
    return this.request<any>(`/intake/sessions/${sessionId}`)
  }

  async updateIntakeSession(sessionId: string, data: { patient_id?: string }) {
    return this.request<any>(`/intake/sessions/${sessionId}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async deleteIntakeSession(sessionId: string) {
    return this.request<void>(`/intake/sessions/${sessionId}`, { method: 'DELETE' })
  }

  async getIntakeSummary(sessionId: string) {
    return this.request<any>(`/intake/sessions/${sessionId}/summary`)
  }

  async getPatientIntakeSessions(patientId: string) {
    return this.request<any[]>(`/intake/sessions/by-patient/${patientId}`)
  }

  // Patient-facing intake methods (no auth required, use token from URL)
  async startIntakeSession(accessToken: string) {
    // Temporarily remove auth for this request
    const currentToken = this.token
    this.token = null
    try {
      return await this.request<any>(`/intake/start/${accessToken}`)
    } finally {
      this.token = currentToken
    }
  }

  async undoIntakeMessage(accessToken: string) {
    const currentToken = this.token
    this.token = null
    try {
      return await this.request<any>(`/intake/undo/${accessToken}`, { method: 'POST' })
    } finally {
      this.token = currentToken
    }
  }

  async verifyIntakePin(accessToken: string, pin: string) {
    const currentToken = this.token
    this.token = null
    try {
      return await this.request<any>(`/intake/verify-pin/${accessToken}`, {
        method: 'POST',
        body: JSON.stringify({ pin })
      })
    } finally {
      this.token = currentToken
    }
  }

  async sendIntakeMessage(accessToken: string, message: string, isVoiceTranscript = false, language = 'en') {
    const currentToken = this.token
    this.token = null
    try {
      return await this.request<any>(`/intake/message/${accessToken}`, {
        method: 'POST',
        body: JSON.stringify({ message, is_voice_transcript: isVoiceTranscript, language })
      })
    } finally {
      this.token = currentToken
    }
  }

  async completeIntakeSession(accessToken: string, aiConsent?: boolean) {
    const currentToken = this.token
    this.token = null
    try {
      return await this.request<any>(`/intake/complete/${accessToken}`, { method: 'POST', body: JSON.stringify({ ai_consent: aiConsent ?? null }) })
    } finally {
      this.token = currentToken
    }
  }
  // ── Schedule ──────────────────────────────────────────────────────────────

  async getSchedule(dateStr: string) {
    return this.request<any[]>(`/schedule-entries?schedule_date=${dateStr}`)
  }

  async bulkImportSchedule(scheduleDate: string, entries: any[], replace = true) {
    return this.request<any[]>('/schedule-entries/bulk', {
      method: 'POST',
      body: JSON.stringify({ schedule_date: scheduleDate, entries, replace })
    })
  }

  async updateScheduleEntry(entryId: string, update: any) {
    return this.request<any>(`/schedule-entries/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(update)
    })
  }

  async deleteScheduleEntry(entryId: string) {
    return this.request<void>(`/schedule-entries/${entryId}`, { method: 'DELETE' })
  }

  async clearScheduleDay(dateStr: string) {
    return this.request<void>(`/schedule-entries?schedule_date=${dateStr}`, { method: 'DELETE' })
  }

  async extractScheduleFromScreenshot(imageBase64: string) {
    return this.request<any>('/schedule/extract-screenshot', {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64, emr_name: null })
    })
  }
  async completeOnboarding(data: {
    specialty: string
    practice_size: string
    current_emr: string
    agree_terms: boolean
    agree_privacy: boolean
    agree_baa: boolean
  }) {
    return this.request<any>('/auth/complete-onboarding', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

}

export const api = new ApiClient()
